/**
 * 현장 통합서버 맵 — offline-manage 콘솔 맵처럼 외곽·내부선, 구역, 기둥, 주차면, 통로등,
 * 비상벨, CCTV 를 그린다. CCTV 마커·글자 크기는 확대와 상관없이 화면 픽셀로 고정하고,
 * 비상벨은 도면 크기로 그려 확대하면 같이 커진다.
 *
 * 조작 (앱에서 쓰므로 터치 우선):
 *  - 주차면 탭 → onSpaceTap (점유 on/off). 끌다가 뗀 것은 탭으로 치지 않는다.
 *  - CCTV 탭(데스크톱은 마우스 올림) → 그 CCTV 에 연결된 주차면 강조
 *  - 한 손가락/마우스 드래그 이동, 두 손가락 핀치·휠 확대, [맞춤]으로 되돌림
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Typography, alpha, useTheme } from '@mui/material';
import { CenterFocusStrong as FitIcon } from '@mui/icons-material';

import { buildFloorShapes, Pt, QaCamera } from './qaMapShapes';
import type { QaInspectMarks } from './qaInspect';
import type { QaCctvLights } from '../../services/SiteParkingQaService';

/** 맵 주차면 이름 → router 주차면 상태. 없으면 router QA 대상이 아니다(CCTV 미연결). */
export interface QaSpaceState {
  occupied: boolean;
  fixed: boolean;
  held: boolean;
}

interface Props {
  mapData: unknown;
  spaces?: Map<string, QaSpaceState>;
  /** CCTV 이름 → 카메라 반영 중지(QA 고정) 여부. */
  heldCctvs?: Set<string>;
  onSpaceTap?: (name: string) => void;
  /** CCTV 를 탭하면 그 이름으로 부른다 (상세 열기). */
  onCameraTap?: (name: string) => void;
  /** 바꾸는 중인 주차면 — 흐리게 그린다. */
  pending?: Set<string>;
  /** CCTV 이름 → 조명 상태. 주면 통로등 밝기·만공차등 점등을 그린다. */
  lights?: Record<string, QaCctvLights>;
  /** 주차면 이름 → 카메라가 본 점유 (최근 판정만). 주면 DB 와 다른 면에 점선을 두른다. */
  camera?: Map<string, boolean>;
  /** CCTV 이름 → 카메라 판정 상태. stale/none 이면 CCTV 를 빨갛게 그린다. */
  cameraHealth?: Map<string, 'ok' | 'stale' | 'none'>;
  /** 확인을 마친 CCTV — 파란 고리를 두른다 (통로등 테스트에서 반응한 CCTV). */
  markedCctvs?: Set<string>;
  /** CCTV 옆에 이름·IP 글자를 쓴다. */
  showCctvName?: boolean;
  showCctvIp?: boolean;
  /** 층 조명이 수동 모드 — 통로등 글자·만공차등 설명에 "수동" 을 붙인다. */
  aisleManual?: boolean;
  parkingManual?: boolean;
  /**
   * 점검 탭(핑·모듈·페어링) — 문제 있는 CCTV·비상벨·통로등만 칠하고 나머지는 회색으로 둔다.
   * 어디가 잘못됐는지 한눈에 보이게 점유·조명·카메라 판정 색은 주지 않는다.
   */
  inspect?: QaInspectMarks;
}

type ViewBox = { x: number; y: number; w: number; h: number };

const PAD = 0.04; // 맵 둘레 여백 (offline-manage lightsmon 과 같다)
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 40;
const TAP_SLOP = 8; // px — 이보다 덜 움직이고 떼면 탭
const MIN_TAP_PX = 14; // 주차면이 화면에서 이보다 작으면 확대하라고 알려 준다
const toPoints = (p: Pt[]) => p.map((q) => `${q[0]},${q[1]}`).join(' ');

// offline-manage map.js 색 — CCTV 는 종류별, 비상벨은 보라.
const CAM_COLOR = { light: '#37d39b', warning: '#d8a23a' } as const;
export const BELL_COLOR = '#ef4444';
const BELL_SPACE_RATIO = 0.25;
export const SPACE_COLOR = { occupied: '#ef4444', empty: '#22c55e', held: '#f59e0b' } as const;
export const MISMATCH_COLOR = '#fde047';
export const LIGHT_COLOR = '#ffd23f';
export const MARK_COLOR = '#3b82f6';
const DEAD_COLOR = '#ef4444';
/** 점검 탭 — 문제(빨강)·확인 필요(주황), 나머지는 회색. */
export const INSPECT_COLOR = { error: '#ef4444', warning: '#f59e0b' } as const;
export const NEUTRAL_COLOR = '#64748b';
const MIN_LABEL_PX = 26; // 통로등이 화면에서 이보다 크면 밝기(%)를 글자로 쓴다

const QaSiteMap: React.FC<Props> = ({
  mapData,
  spaces,
  heldCctvs,
  onSpaceTap,
  onCameraTap,
  pending,
  lights,
  camera,
  cameraHealth,
  markedCctvs,
  showCctvName,
  showCctvIp,
  aisleManual,
  parkingManual,
  inspect,
}) => {
  const theme = useTheme();
  const shapes = useMemo(() => buildFloorShapes(mapData), [mapData]);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [size, setSize] = useState({ w: 1, h: 1 });
  const [focus, setFocus] = useState<QaCamera | null>(null);

  const base = useMemo<ViewBox | null>(() => {
    const b = shapes.bounds;
    if (!b) return null;
    const w = Math.max(b.maxX - b.minX, 1);
    const h = Math.max(b.maxY - b.minY, 1);
    return { x: b.minX - w * PAD, y: b.minY - h * PAD, w: w * (1 + 2 * PAD), h: h * (1 + 2 * PAD) };
  }, [shapes]);
  const [vb, setVb] = useState<ViewBox | null>(base);
  useEffect(() => setVb(base), [base]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const update = () => setSize({ w: el.clientWidth || 1, h: el.clientHeight || 1 });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [base]);

  // 화면 1px 이 맵 몇 단위인지 (preserveAspectRatio meet 이라 더 큰 쪽).
  const unit = vb ? Math.max(vb.w / size.w, vb.h / size.h) : 1;

  /** 화면 좌표 (cx,cy) 를 중심으로 factor 배 (>1 이면 축소). */
  const zoomAt = (cur: ViewBox, cx: number, cy: number, factor: number): ViewBox => {
    const el = svgRef.current;
    if (!el || !base) return cur;
    const rect = el.getBoundingClientRect();
    const u = Math.max(cur.w / rect.width, cur.h / rect.height);
    // meet 정렬로 생기는 여백을 빼고 화면 점 아래의 맵 좌표를 구한다.
    const mx = cur.x - (rect.width * u - cur.w) / 2 + (cx - rect.left) * u;
    const my = cur.y - (rect.height * u - cur.h) / 2 + (cy - rect.top) * u;
    const zoom = base.w / (cur.w * factor);
    if (zoom < MIN_ZOOM || zoom > MAX_ZOOM) return cur;
    return {
      x: mx - (mx - cur.x) * factor,
      y: my - (my - cur.y) * factor,
      w: cur.w * factor,
      h: cur.h * factor,
    };
  };
  const zoomRef = useRef(zoomAt);
  zoomRef.current = zoomAt;

  // 휠은 React 의 passive 리스너로는 preventDefault 가 안 돼 직접 붙인다.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setVb((cur) =>
        cur ? zoomRef.current(cur, e.clientX, e.clientY, e.deltaY < 0 ? 1 / 1.25 : 1.25) : cur,
      );
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [base]);

  // 포인터 — 하나면 이동/탭, 둘이면 핀치.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{
    start: { x: number; y: number };
    vb: ViewBox;
    u: number;
    moved: boolean;
    target: { space?: string; camera?: string };
    pinch?: { dist: number; vb: ViewBox };
  } | null>(null);

  const targetOf = (el: EventTarget | null, x: number, y: number) => {
    const hit = el as Element | null;
    // CCTV 근처를 누르면 손가락 아래의 모든 것을 본다 — 촘촘한 현장(중외 2BL)에서는 CCTV 둘레의
    // 넓은 터치 영역이 옆 CCTV 점과 주차면을 덮는다(맨 위 요소만 보면 엉뚱한 CCTV 가 열린다).
    //  1) CCTV 점 자체를 눌렀으면 그 CCTV (여럿이면 중심이 가장 가까운 것)
    //  2) 터치 영역만 눌렀고 그 아래 주차면이 있으면 주차면
    //  3) 아니면 터치 영역이 겹친 CCTV 중 중심이 가장 가까운 것
    if (hit?.closest?.('[data-camera]') && typeof document.elementsFromPoint === 'function') {
      const els = document.elementsFromPoint(x, y);
      const nearest = (groups: Element[]) => {
        let best: Element | null = null;
        let bestD = Infinity;
        groups.forEach((g) => {
          const r = g.querySelector('[data-dot]')?.getBoundingClientRect();
          if (!r) return;
          const d = Math.hypot(r.x + r.width / 2 - x, r.y + r.height / 2 - y);
          if (d < bestD) [best, bestD] = [g, d];
        });
        return (best as Element | null)?.getAttribute('data-camera') ?? undefined;
      };
      const groupsOf = (pred: (n: Element) => boolean) =>
        Array.from(
          new Set(
            els
              .filter(pred)
              .map((n) => n.closest('[data-camera]'))
              .filter((g): g is Element => !!g),
          ),
        );
      const dotted = groupsOf((n) => n.hasAttribute('data-dot'));
      if (dotted.length) return { camera: nearest(dotted) };
      const under = els.find((n) => n.hasAttribute('data-space'));
      if (under) return { space: under.getAttribute('data-space') ?? undefined };
      return { camera: nearest(groupsOf(() => true)) };
    }
    const node = hit?.closest?.('[data-space],[data-camera]');
    return {
      space: node?.getAttribute('data-space') ?? undefined,
      camera: node?.getAttribute('data-camera') ?? undefined,
    };
  };
  const pinchDist = () => {
    const [a, b] = Array.from(pointers.current.values());
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!vb) return;
    svgRef.current?.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && gesture.current) {
      gesture.current.moved = true; // 핀치는 탭이 아니다
      gesture.current.pinch = { dist: pinchDist(), vb };
      return;
    }
    gesture.current = {
      start: { x: e.clientX, y: e.clientY },
      vb,
      u: unit,
      moved: false,
      target: targetOf(e.target, e.clientX, e.clientY),
    };
  };
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const g = gesture.current;
    if (!g || !pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pinch && pointers.current.size >= 2) {
      const [a, b] = Array.from(pointers.current.values());
      setVb(
        zoomAt(
          g.pinch.vb,
          (a.x + b.x) / 2,
          (a.y + b.y) / 2,
          g.pinch.dist / Math.max(pinchDist(), 1),
        ),
      );
      return;
    }
    const dx = e.clientX - g.start.x;
    const dy = e.clientY - g.start.y;
    if (!g.moved && Math.hypot(dx, dy) < TAP_SLOP) return;
    g.moved = true;
    setVb({ ...g.vb, x: g.vb.x - dx * g.u, y: g.vb.y - dy * g.u });
  };
  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (!g) return;
    if (pointers.current.size > 0) {
      // 핀치에서 한 손가락만 뗐다 — 남은 손가락으로 이어서 이동한다.
      const [rest] = Array.from(pointers.current.values());
      gesture.current = { start: rest, vb: vb ?? g.vb, u: unit, moved: true, target: {} };
      return;
    }
    gesture.current = null;
    if (g.moved || e.type !== 'pointerup') return;
    if (g.target.space && onSpaceTap) {
      onSpaceTap(g.target.space);
    } else if (g.target.camera) {
      // 탭한 CCTV 를 고른다 (마우스는 올릴 때 이미 골라져 있다). 빈 곳을 탭하면 푼다.
      const cam = shapes.cameras.find((c) => `${c.kind}${c.id}` === g.target.camera) ?? null;
      setFocus(cam);
      if (cam && onCameraTap) onCameraTap(cam.name);
    } else {
      setFocus(null);
    }
  };

  const linked = useMemo(() => new Set(focus?.linked ?? []), [focus]);
  // 주차면 짧은 변의 중앙값(맵 단위) — 화면에서 손가락으로 누를 만한 크기인지 본다.
  const spaceSize = useMemo(() => {
    const sides = shapes.parking
      .map((p) => {
        const xs = p.pts.map((q) => q[0]);
        const ys = p.pts.map((q) => q[1]);
        return Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
      })
      .sort((a, b) => a - b);
    return sides.length ? sides[Math.floor(sides.length / 2)] : 0;
  }, [shapes]);
  // 비상벨 반지름(맵 단위) — 주차면 짧은 변의 1/4. 주차면이 없는 층은 화면 고정 크기로 둔다.
  const bellR = spaceSize > 0 ? spaceSize * BELL_SPACE_RATIO : 5.5 * unit;

  if (!base || !vb) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">이 층에는 그릴 맵 데이터가 없습니다.</Typography>
      </Box>
    );
  }

  const line = alpha(theme.palette.text.primary, 0.45);
  const faint = alpha(theme.palette.text.primary, 0.25);
  const bg = theme.palette.mode === 'dark' ? '#0d1117' : '#f6f8fa';
  const r = 5 * unit;

  const spaceStyle = (name: string, id: string) => {
    const st = spaces?.get(name);
    const isLinked = linked.has(name) || linked.has(id);
    if (!st) {
      return {
        fill: isLinked ? alpha('#2dd4bf', 0.35) : alpha(theme.palette.text.primary, 0.05),
        stroke: isLinked ? '#2dd4bf' : faint,
        width: 1,
      };
    }
    const color = st.occupied ? SPACE_COLOR.occupied : SPACE_COLOR.empty;
    const seen = camera?.get(name);
    const mismatch = seen !== undefined && seen !== st.occupied;
    return {
      fill: alpha(color, st.occupied ? 0.6 : 0.3),
      stroke: isLinked ? '#2dd4bf' : mismatch ? MISMATCH_COLOR : st.held ? SPACE_COLOR.held : color,
      width: isLinked || st.held || mismatch ? 2.5 : 1,
      dash: mismatch && !isLinked ? '5 3' : undefined,
      mismatch,
    };
  };

  /**
   * 통로등 — 켜진 것만 밝기 그대로(30% 면 30% 진한 노랑) 칠하고, 꺼진 것은 비워 둔다.
   * 상태를 모르면 옅게. 점검 탭에선 등록이 꼬인 CCTV 의 통로등만 주황, 나머지는 비운다.
   */
  const lightStyle = (cctv?: string) => {
    if (inspect) {
      return cctv && inspect.lightCctvs.has(cctv)
        ? { fill: alpha(INSPECT_COLOR.warning, 0.55), stroke: INSPECT_COLOR.warning, value: null }
        : { fill: 'transparent', stroke: faint, value: null };
    }
    if (!lights)
      return { fill: alpha(LIGHT_COLOR, 0.12), stroke: alpha(LIGHT_COLOR, 0.4), value: null };
    const l = cctv ? lights[cctv] : undefined;
    if (!l || !l.aisleTotal) {
      return { fill: alpha('#94a3b8', 0.1), stroke: alpha('#94a3b8', 0.4), value: null };
    }
    const v = l.aisleOn ? l.aisleValue : 0;
    return v > 0
      ? { fill: alpha(LIGHT_COLOR, Math.min(100, v) / 100), stroke: LIGHT_COLOR, value: v }
      : { fill: 'transparent', stroke: faint, value: 0 };
  };
  const bellTone = (name: string, customId: string) =>
    inspect?.bells.get(name) ?? inspect?.bells.get(customId);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component="svg"
        ref={svgRef}
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="qa-site-map"
        sx={{
          display: 'block',
          width: '100%',
          // 맵 비율대로 높이를 잡되 화면을 넘지 않게 — 휴대폰에서 위아래 빈 공간이 생기지 않는다.
          aspectRatio: `${base.w} / ${base.h}`,
          maxHeight: { xs: '68svh', md: 640 },
          minHeight: 300,
          bgcolor: bg,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          cursor: 'grab',
          touchAction: 'none',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          '& *': { vectorEffect: 'non-scaling-stroke' },
          '& [data-space]': { cursor: onSpaceTap ? 'pointer' : 'inherit' },
          '& [data-camera]': { cursor: 'pointer' },
        }}
      >
        <g fill="none" stroke={line} strokeWidth={1} pointerEvents="none">
          {shapes.lines.map((p, i) => (
            <polyline key={`l${i}`} points={toPoints(p)} />
          ))}
        </g>
        <g stroke={faint} strokeWidth={1} pointerEvents="none">
          {shapes.zones.map((p) => (
            <polygon
              key={`z${p.id}`}
              points={toPoints(p.pts)}
              fill={alpha(theme.palette.primary.main, 0.04)}
            />
          ))}
          {shapes.columns.map((p) => (
            <polygon
              key={`c${p.id}`}
              points={toPoints(p.pts)}
              fill={alpha(theme.palette.text.primary, 0.18)}
            />
          ))}
        </g>
        <g pointerEvents="none">
          {shapes.lights.map((p) => {
            const ls = lightStyle(p.cctv);
            const xs = p.pts.map((q) => q[0]);
            const ys = p.pts.map((q) => q[1]);
            const big =
              Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) /
                unit >=
              MIN_LABEL_PX;
            return (
              <g key={`li${p.id}`} data-testid="qa-map-light" data-value={ls.value ?? ''}>
                <polygon
                  points={toPoints(p.pts)}
                  fill={ls.fill}
                  stroke={ls.stroke}
                  strokeWidth={1}
                />
                {ls.value !== null && ls.value > 0 && big && (
                  <text
                    x={(Math.max(...xs) + Math.min(...xs)) / 2}
                    y={(Math.max(...ys) + Math.min(...ys)) / 2}
                    fontSize={11 * unit}
                    fontWeight={700}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    // 옅은 노랑 위엔 바탕 글자색이, 진한 노랑 위엔 어두운 글자가 읽힌다.
                    fill={ls.value >= 50 ? '#1f2937' : theme.palette.text.primary}
                  >
                    {`${aisleManual ? '수동 ' : ''}${ls.value}%`}
                  </text>
                )}
              </g>
            );
          })}
        </g>
        <g>
          {shapes.parking.map((p) => {
            const s = spaceStyle(p.name, p.id);
            const st = spaces?.get(p.name);
            return (
              <polygon
                key={`p${p.id}`}
                points={toPoints(p.pts)}
                fill={s.fill}
                stroke={s.stroke}
                strokeWidth={s.width}
                strokeDasharray={s.dash}
                opacity={pending?.has(p.name) ? 0.4 : 1}
                data-space={st ? p.name : undefined}
                data-testid="qa-map-parking"
                data-state={st ? (st.occupied ? 'occupied' : 'empty') : 'none'}
                data-mismatch={s.mismatch ? '1' : undefined}
              >
                <title>{`${p.name}${st ? (st.occupied ? ' · 점유' : ' · 빈자리') : ' · CCTV 미연결'}${
                  s.mismatch ? ' · 카메라 판정과 다름' : ''
                }`}</title>
              </polygon>
            );
          })}
        </g>
        {/* 비상벨 — 속이 찬 빨간 원. 다른 마커와 달리 도면 크기(주차면 짧은 변 기준)로 그려
            확대하면 도면과 같이 커진다. */}
        <g fill={BELL_COLOR} pointerEvents="none">
          {shapes.bells.map((p, i) => {
            const tone = bellTone(p.name, p.customId);
            return (
              <circle
                key={`b${i}`}
                cx={p.x}
                cy={p.y}
                r={bellR}
                // 점검 탭 — 문제 벨만 색, 나머지는 회색.
                fill={inspect ? (tone ? INSPECT_COLOR[tone] : alpha(NEUTRAL_COLOR, 0.35)) : undefined}
                data-tone={inspect ? tone ?? 'none' : undefined}
                data-testid="qa-map-bell"
              >
                {p.name && <title>{`${p.name}${p.customId ? ` · 벨 ID ${p.customId}` : ''}`}</title>}
              </circle>
            );
          })}
        </g>
        <g>
          {shapes.cameras.map((c) => {
            const key = `${c.kind}${c.id}`;
            const on = focus === c;
            const health = cameraHealth?.get(c.name);
            const dead = c.kind === 'light' && (health === 'stale' || health === 'none');
            const marked = !!markedCctvs?.has(c.name);
            const pl = lights?.[c.name];
            const tone = inspect?.cctvs.get(c.name);
            return (
              <g
                key={key}
                data-camera={key}
                onPointerEnter={(e) => e.pointerType === 'mouse' && setFocus(c)}
                onPointerLeave={(e) =>
                  e.pointerType === 'mouse' && setFocus((h) => (h === c ? null : h))
                }
                data-testid="qa-map-cctv"
              >
                {/* 손가락으로 누르기 쉽게 보이지 않는 넓은 영역 */}
                <circle cx={c.x} cy={c.y} r={r * 2.6} fill="transparent" data-hit="1" />
                {heldCctvs?.has(c.name) && (
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r={r * 1.9}
                    fill="none"
                    stroke={SPACE_COLOR.held}
                    strokeWidth={2}
                  />
                )}
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={on ? r * 1.5 : r}
                  // 통로등이 반응한 CCTV 는 고리를 따로 두르지 않고 점 자체를 파랗게 칠한다.
                  // markedCctvs 는 통로등 탭에서만 오므로, 그 탭에선 주차 판정 끊김(빨강)보다 반응이 먼저다.
                  fill={
                    inspect
                      ? tone
                        ? INSPECT_COLOR[tone]
                        : NEUTRAL_COLOR
                      : marked
                        ? MARK_COLOR
                        : dead
                          ? DEAD_COLOR
                          : CAM_COLOR[c.kind]
                  }
                  data-tone={inspect ? tone ?? 'none' : undefined}
                  data-dot="1"
                  data-testid={marked ? 'qa-map-cctv-mark' : undefined}
                  stroke={theme.palette.background.paper}
                  strokeWidth={1.5}
                  data-health={health}
                />
                {pl && pl.parkingTotal > 0 && (
                  // 만공차등 — CCTV 오른쪽 위 작은 네모. 켜짐(공차)은 초록, 꺼짐(만차 소등)은 어둡게.
                  <rect
                    x={c.x + r * 1.1}
                    y={c.y - r * 2.6}
                    width={r * 1.6}
                    height={r * 1.6}
                    rx={r * 0.3}
                    fill={
                      pl.parkingOn
                        ? alpha('#22c55e', 0.35 + (0.65 * pl.parkingValue) / 100)
                        : '#334155'
                    }
                    stroke={theme.palette.background.paper}
                    strokeWidth={1}
                    data-testid="qa-map-parking-light"
                    data-on={pl.parkingOn ? '1' : '0'}
                  />
                )}
                <title>{`${c.name}${c.ip ? ` · ${c.ip}` : ''}${
                  dead
                    ? health === 'none'
                      ? ' · 카메라 판정 없음'
                      : ' · 카메라 판정 30초 넘음'
                    : ''
                }${
                  pl && pl.parkingTotal
                    ? ` · 만공차등 ${parkingManual ? '수동 ' : ''}${pl.parkingOn ? `켜짐 ${pl.parkingValue}%` : '꺼짐'}`
                    : ''
                }`}</title>
              </g>
            );
          })}
        </g>
        {(showCctvName || showCctvIp) && (
          <g
            fill={theme.palette.text.primary}
            fontSize={10 * unit}
            fontWeight={600}
            textAnchor="middle"
            stroke={bg}
            strokeWidth={3}
            paintOrder="stroke"
            pointerEvents="none"
          >
            {shapes.cameras.map((c) => {
              const lines = [showCctvName ? c.name : '', showCctvIp ? c.ip : ''].filter(Boolean);
              return lines.map((t, i) => (
                <text
                  key={`${c.kind}${c.id}-${i}`}
                  x={c.x}
                  y={c.y + r * 2.4 + (i + 0.8) * 11 * unit}
                  data-testid="qa-map-cctv-label"
                >
                  {t}
                </text>
              ));
            })}
          </g>
        )}
        <g
          fill="#ef4444"
          fontSize={12 * unit}
          fontWeight={700}
          textAnchor="middle"
          dominantBaseline="middle"
          stroke={bg}
          strokeWidth={3}
          paintOrder="stroke"
          pointerEvents="none"
        >
          {shapes.labels.map((l, i) => (
            <text key={`t${i}`} x={l.x} y={l.y}>
              {l.text}
            </text>
          ))}
        </g>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          right: 84,
          pointerEvents: 'none',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          bgcolor: alpha(bg, 0.8),
          width: 'fit-content',
          maxWidth: 'calc(100% - 92px)',
        }}
      >
        <Typography variant="caption" color="text.secondary" noWrap>
          {focus ? (
            <Typography component="span" variant="caption" color="text.primary" fontWeight={700}>
              {focus.name}
              {focus.ip ? ` (${focus.ip})` : ''} · 연결 {focus.linked.length}면
            </Typography>
          ) : (
            <>
              {`주차면 ${shapes.parking.length} · CCTV ${shapes.cameras.length}`}
              {onSpaceTap && spaceSize / unit < MIN_TAP_PX && (
                <Typography
                  component="span"
                  variant="caption"
                  color="warning.main"
                  fontWeight={700}
                >
                  {' '}
                  · 확대해서 누르세요
                </Typography>
              )}
            </>
          )}
        </Typography>
      </Stack>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FitIcon fontSize="small" />}
        onClick={() => setVb(base)}
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          textTransform: 'none',
          bgcolor: 'background.paper',
          minHeight: 36,
        }}
      >
        맞춤
      </Button>
    </Box>
  );
};

export default QaSiteMap;
